import {
	NextButton,
	PrevButton,
} from '@components/carousel-buttons/carousel-buttons';
import dealsConstants from '@pages/Deals/constants';
import { useAppSelector } from '@redux/hooks';
import { selectIsMobile } from '@redux/viewport/selectors';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { TextBody, TextCaption, TextTitle } from '@tkww/orion-web-ds-text';
import type { Decorated } from '@typings/vendor';
import { type Deal, dealTypeInfo } from '@typings/vendor/properties';
import { formatDate } from '@utils/dates';
import { compose } from '@xo-union/react-css-modules';
import { Carousel } from '@xo-union/ui-carousel';
import classNames from 'classnames';
import React, { useEffect, type FC } from 'react';
import { useRef, useState } from 'react';
import type { NavItemProps } from '../nav/types';
import { DealsModal } from './deals_modal';
import Styles from './styles.scss';

export interface DealCardProps {
	deal: Deal;
	index: number;
	extraClass?: string;
	onOpenModal: (index: number) => void;
}

export interface DealsProps extends NavItemProps {
	deals: Deal[];
}

const DealCard: FC<DealCardProps> = ({
	deal,
	index,
	extraClass,
	onOpenModal,
}) => {
	return (
		<div
			className={classNames(Styles.dealCard, extraClass && Styles[extraClass])}
			data-testid="deal-card"
			{...{ onClick: () => onOpenModal(index) }}
		>
			<div className={Styles.iconWrapper}>
				<Icon
					size="lg"
					aria-label={undefined}
					aria-hidden={true}
					name="rating-expressive"
					className={Styles.iconBackground}
				/>
				<Icon
					name={dealTypeInfo[deal.type].icon}
					aria-label={undefined}
					aria-hidden={true}
					size="md"
					className={Styles.icon}
				/>
			</div>
			<div className={Styles.infoWrapper}>
				<TextCaption className={Styles.dealInfo}>
					{dealTypeInfo[deal.type].label}
					<span className={Styles.dealSeparator} />
					{deal.endDate
						? `Expires on ${formatDate(deal.endDate)}`
						: 'Permanent'}
				</TextCaption>
				<TextBody size="md" bold className={Styles.dealTitle}>
					{deal.title}
				</TextBody>
			</div>
			{
				<TextBody size="md" className={Styles.openDealButton} color="info">
					{dealsConstants.DEAL_CTA_TEXT}
				</TextBody>
			}
		</div>
	);
};

export const Deals: FC<DealsProps> = ({ deals }) => {
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const isMobile = useAppSelector((state) => selectIsMobile(state));
	const [carouselIndex, setCarouselIndex] = useState(0);
	const [slideWidth, setSlideWidth] = useState(272);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [dealIndex, setDealIndex] = useState(0);
	const containerRef = useRef<HTMLDivElement | null>(null);

	const onNavigationIndexChange = (nextIndex: number) => {
		setCarouselIndex(nextIndex);
	};

	const handleOpenModal = (index: number) => {
		setDealIndex(index);
		setIsModalOpen(true);
	};

	const maxSlidesInView = isMobile ? 1 : 3;
	const slidesToShow = Math.min(deals.length, maxSlidesInView);
	const slideGutter = 16;
	const peekNextSlidePx = isMobile && deals.length > 1 ? 16 : 0; // pixels of next slide visible
	useEffect(() => {
		const updateSlideWidth = () => {
			const container = containerRef.current;
			if (!container) return;

			const totalWidth = container.offsetWidth;
			const totalGutterWidth = slidesToShow * slideGutter;
			const cardWidth =
				(totalWidth - totalGutterWidth) / slidesToShow - peekNextSlidePx;
			setSlideWidth(cardWidth);
		};

		updateSlideWidth();
		window.addEventListener('resize', updateSlideWidth);
		return () => window.removeEventListener('resize', updateSlideWidth);
	}, [slidesToShow, peekNextSlidePx]);

	const carouselStyles = compose({
		container: Styles.dealsSliderWrapper,
	});

	return (
		<div className={Styles.dealsContainer} ref={containerRef}>
			<TextTitle size="sm" className={Styles.title}>
				Deals
			</TextTitle>
			{deals.length > slidesToShow ? (
				<Carousel
					classes={carouselStyles}
					gutter={slideGutter}
					minSlideWidth={slideWidth}
					maxSlideWidth={slideWidth}
					renderNextButton={NextButton}
					renderPrevButton={PrevButton}
					onNavigationIndexChange={onNavigationIndexChange}
					navigationIndex={carouselIndex}
				>
					{deals.map((deal, index) => (
						<DealCard
							deal={deal}
							index={index}
							key={deal.id}
							onOpenModal={handleOpenModal}
						/>
					))}
				</Carousel>
			) : (
				<div className={Styles.dealsStaticWrapper}>
					{deals.map((deal, index) => (
						<DealCard
							deal={deal}
							index={index}
							key={deal.id}
							extraClass={deals.length === 1 ? 'singleCard' : ''}
							onOpenModal={handleOpenModal}
						/>
					))}
				</div>
			)}
			{isModalOpen && (
				<DealsModal
					vendorName={vendor.name}
					selectedDealIndex={dealIndex}
					deals={deals}
					onClose={() => setIsModalOpen(false)}
				/>
			)}
		</div>
	);
};
